import type {
  DunningBulkCancelInput,
  DunningBulkResultDto,
  DunningBulkValidateInput,
  DunningChannel,
  DunningEmailPreviewDto,
  DunningEmailPreviewInput,
  DunningEmailTemplateDto,
  DunningNoticeDto,
  DunningNoticeStatus,
  DunningSendingDto,
  DunningSummaryDto,
  DunningTemplatesReplaceInput,
  DunningTotalsDto,
} from '@osteopro/shared';

/** Les trois niveaux de relance, tels que le contrat les borne. */
export type DunningLevel = 1 | 2 | 3;
export const DUNNING_LEVELS: readonly DunningLevel[] = [1, 2, 3];
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../../lib/api.js';
import { runAuthed } from '../../lib/auth-store.js';
import { billingKeys } from '../../lib/query-keys.js';

/**
 * Relances de paiement.
 *
 * Une relance naît en `pending_validation` : la V1 envoyait depuis une tâche automatique sans
 * qu'aucun praticien ne relise la lettre. Le passage à `sent` est une action explicite, et c'est
 * elle qui choisit le canal.
 */

export const DUNNING_PAGE_SIZE = 20;

export interface DunningListPage {
  items: DunningNoticeDto[];
  page: number;
  pageSize: number;
  total: number;
  /**
   * Montants **du filtre entier**, pas de la page. Les resommer dans le navigateur donnerait un
   * total faux dès la deuxième page : c'est exactement le défaut que le contrat évite.
   */
  totals: DunningTotalsDto;
}

export interface DunningListParams {
  status?: DunningNoticeStatus;
  /** Relances d'une facture donnée. */
  invoiceId?: string;
  /** Relances des factures d'une consultation : une relance n'en porte pas elle-même. */
  encounterId?: string;
  page?: number;
}

interface DunningListRpcQuery {
  page: string;
  pageSize: string;
  status?: DunningNoticeStatus;
  invoiceId?: string;
  encounterId?: string;
}

function listQuery(params: DunningListParams): DunningListRpcQuery {
  return {
    page: String(params.page ?? 1),
    pageSize: String(DUNNING_PAGE_SIZE),
    ...(params.status === undefined ? {} : { status: params.status }),
    ...(params.invoiceId === undefined ? {} : { invoiceId: params.invoiceId }),
    ...(params.encounterId === undefined ? {} : { encounterId: params.encounterId }),
  };
}

export function useDunningNotices(params: DunningListParams) {
  return useQuery({
    queryKey: billingKeys.dunningList(params),
    queryFn: async (): Promise<DunningListPage> =>
      runAuthed(async () => unwrap(await api.api.dunning.$get({ query: listQuery(params) }))),
  });
}

/** Intervalle des badges de relances, repris de la V1. */
export const DUNNING_SUMMARY_REFETCH_MS = 60_000;

/**
 * Résumé du recouvrement : à relancer, à valider, envoyées. Une seule requête de comptes, sans
 * lignes, pour l'en-tête de l'onglet Relances et les deux badges, qui lisent donc le même chiffre.
 */
export function useDunningSummary(options?: { readonly enabled?: boolean }) {
  return useQuery({
    queryKey: billingKeys.dunningSummary(),
    refetchInterval: DUNNING_SUMMARY_REFETCH_MS,
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<DunningSummaryDto> => {
      const result = await runAuthed(async () => unwrap(await api.api.dunning.summary.$get()));
      return result.summary;
    },
  });
}

/**
 * Ce qui attend un geste du cabinet : les relances à valider, et les factures à relancer pour le
 * propriétaire, seul à pouvoir lancer la génération. Un badge qui compterait ce qu'on ne peut pas
 * faire ne dirait rien d'utile.
 */
export function dunningTodoCount(summary: DunningSummaryDto, isOwner: boolean): number {
  return summary.pendingValidation.count + (isOwner ? summary.toSend.count : 0);
}

export interface ValidateDunningVariables {
  dunningId: string;
  channel: DunningChannel;
}

/**
 * Validation. `email` est une intention : sans adresse chez le patient ou sans fournisseur
 * d'email, le serveur bascule sur `download` et le dit dans sa réponse — la lettre reste à
 * imprimer. L'appelant lit donc le canal renvoyé, pas celui qu'il a demandé.
 */
export function useValidateDunningNotice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dunningId,
      channel,
    }: ValidateDunningVariables): Promise<DunningNoticeDto> => {
      const result = await runAuthed(async () =>
        unwrap(
          await api.api.dunning[':id'].validate.$post({
            param: { id: dunningId },
            json: { channel },
          }),
        ),
      );
      return result.dunningNotice;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

/**
 * Renvoi d'une relance déjà envoyée. **Sans corps** : le canal se résout comme au premier envoi,
 * courriel si le dossier porte une adresse, téléchargement sinon — le praticien ne choisit pas un
 * canal que le dossier ne permet pas.
 *
 * Le renvoi ajoute une ligne d'historique et fait passer `sentAt` et `channel` au **dernier**
 * envoi. Le délai avant le niveau suivant repart donc de là, et c'est voulu : on n'escalade pas
 * à partir d'une lettre qui n'est jamais arrivée.
 */
export function useResendDunningNotice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dunningId: string): Promise<DunningSendingDto> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning[':id'].resend.$post({ param: { id: dunningId } })),
      );
      return result.sending;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

export function useCancelDunningNotice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dunningId: string): Promise<DunningNoticeDto> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning[':id'].cancel.$post({ param: { id: dunningId } })),
      );
      return result.dunningNotice;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

/**
 * Relance posée **à la main sur une facture précise**, au niveau choisi. Le balayage automatique ne
 * sait faire que « toutes les factures éligibles au niveau suivant » ; un praticien qui vient
 * d'avoir le patient au téléphone veut relancer celui-ci, maintenant.
 */
export interface CreateDunningVariables {
  invoiceId: string;
  level: DunningLevel;
}

export function useCreateDunningNotice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDunningVariables): Promise<DunningNoticeDto> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning.$post({ json: input })),
      );
      return result.dunningNotice;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

/**
 * Validation ou annulation **groupée**. Une boucle côté client serait N appels sans progression
 * fiable : un onglet fermé au milieu laisserait la moitié des relances dans un état que personne
 * ne sait nommer. Un seul appel, et un compte rendu par relance.
 */
export function useBulkValidateDunning() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DunningBulkValidateInput): Promise<DunningBulkResultDto> =>
      runAuthed(async () => unwrap(await api.api.dunning['bulk-validate'].$post({ json: input }))),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

export function useBulkCancelDunning() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DunningBulkCancelInput): Promise<DunningBulkResultDto> =>
      runAuthed(async () => unwrap(await api.api.dunning['bulk-cancel'].$post({ json: input }))),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all() });
    },
  });
}

/**
 * Historique d'envoi d'une relance. `sentAt` et `channel` sur la relance disent le **dernier** ;
 * ici on a les précédents, qu'un renvoi écrasait. Sur une procédure de recouvrement, savoir
 * combien de fois on a relancé et par quel canal est précisément ce qu'on doit montrer.
 */
export function useDunningSendings(dunningId: string | null) {
  return useQuery({
    queryKey: billingKeys.dunningSendings(dunningId),
    enabled: dunningId !== null,
    queryFn: async (): Promise<DunningSendingDto[]> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning[':id'].sendings.$get({ param: { id: dunningId ?? '' } })),
      );
      return result.items;
    },
  });
}

/**
 * Modèles de courriel du cabinet. `PUT` de remplacement complet : une langue absente, ou un
 * niveau absent, revient au texte du produit dans la langue du dossier.
 */
export function useDunningTemplates() {
  return useQuery({
    queryKey: billingKeys.dunningTemplates(),
    queryFn: async (): Promise<DunningEmailTemplateDto[]> => {
      const result = await runAuthed(async () => unwrap(await api.api.dunning.templates.$get()));
      return result.templates;
    },
  });
}

export function useSaveDunningTemplates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DunningTemplatesReplaceInput): Promise<DunningEmailTemplateDto[]> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning.templates.$put({ json: input })),
      );
      return result.templates;
    },
    onSuccess: (templates) => {
      queryClient.setQueryData(billingKeys.dunningTemplates(), templates);
    },
  });
}

/**
 * Aperçu des textes non enregistrés, par la même fonction que l'envoi. L'appelant passe une
 * valeur déjà retardée ; l'aperçu précédent reste affiché le temps que le nouveau arrive.
 */
export function useDunningEmailPreview(input: DunningEmailPreviewInput) {
  return useQuery({
    queryKey: [...billingKeys.dunningTemplates(), 'preview', input] as const,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<DunningEmailPreviewDto> => {
      const result = await runAuthed(async () =>
        unwrap(await api.api.dunning.templates.preview.$post({ json: input })),
      );
      return result.preview;
    },
  });
}
